import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { ApiError } from '@formosa/shared/api/createClient';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { MARKET_ASSET_CLASS_LABELS } from '../products/productConstants';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import FuturesRoundConfigModal from './FuturesRoundConfigModal';
import FuturesRoundBatchConfigModal from './FuturesRoundBatchConfigModal';
import { isRoundConfigurable, isRoundEnded } from './futuresRoundConfigOptions';
import {
  FUTURES_ASSET_CLASS_OPTIONS,
  FUTURES_OVERRIDE_OPTIONS,
  FUTURES_ROUND_PERIODS,
  FUTURES_ROUND_STATE_OPTIONS,
  FUTURES_ROUND_STATE_LABELS,
  FUTURES_ROUND_PERIOD_LABELS,
  MAX_CONTRACT_QUERY_DAYS,
  OVERRIDE_LABELS,
  buildPageParams,
  defaultContractRange,
  formatAmount,
  inputDateTimeToBodyApi,
  validateContractRange,
} from './futuresRoundConstants';

const DEFAULT_RANGE = defaultContractRange();

const EMPTY_FILTERS = {
  marketSymbolCode: '',
  periodType: '',
  state: '',
  roundCode: '',
  overrideMethod: '',
  assetClass: '',
  playType: 'FUTURES',
  contractTimeFromInput: DEFAULT_RANGE.contractTimeFromInput,
  contractTimeToInput: DEFAULT_RANGE.contractTimeToInput,
};

export default function FuturesRoundsPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState('');
  const [settlingCode, setSettlingCode] = useState('');
  const [settleConfirmCode, setSettleConfirmCode] = useState('');
  const [symbolOptions, setSymbolOptions] = useState([]);
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [configOpen, setConfigOpen] = useState(false);
  const [singleConfigCode, setSingleConfigCode] = useState('');
  const [syncing, setSyncing] = useState(false);

  const configurableSelectedCodes = useMemo(() => {
    const configurable = new Set(
      records.filter((row) => isRoundConfigurable(row.state)).map((row) => row.roundCode)
    );
    return selectedCodes.filter((code) => configurable.has(code));
  }, [records, selectedCodes]);

  const allConfigurableSelected = useMemo(() => {
    const configurableRows = records.filter((row) => isRoundConfigurable(row.state));
    return (
      configurableRows.length > 0
      && configurableRows.every((row) => selectedCodes.includes(row.roundCode))
    );
  }, [records, selectedCodes]);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.futuresRoundsPage(buildPageParams(pageNo, nextFilters));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setSelectedCodes([]);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    adminApi
      .orderProductMarketSymbols({ playType: 'FUTURES' })
      .then((list) => setSymbolOptions(list || []))
      .catch(() => setSymbolOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleContractFromChange(value) {
    setFilters((prev) => ({ ...prev, contractTimeFromInput: value }));
  }

  function handleContractToChange(value) {
    setFilters((prev) => ({ ...prev, contractTimeToInput: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    const rangeError = validateContractRange(
      filters.contractTimeFromInput,
      filters.contractTimeToInput
    );
    if (rangeError) {
      setError(rangeError);
      return;
    }
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    loadRecords(1, reset);
  }

  function toggleSelectAll(checked) {
    if (checked) {
      setSelectedCodes(
        records.filter((row) => isRoundConfigurable(row.state)).map((row) => row.roundCode)
      );
      return;
    }
    setSelectedCodes([]);
  }

  function toggleRow(row, checked) {
    if (!isRoundConfigurable(row.state)) {
      return;
    }
    const code = row.roundCode;
    setSelectedCodes((prev) => {
      if (checked) {
        return prev.includes(code) ? prev : [...prev, code];
      }
      return prev.filter((c) => c !== code);
    });
  }

  async function handleSettle(code, force = false) {
    setActionMsg('');
    setSettlingCode(code);
    try {
      const result = await adminApi.settlePendingRound({ roundCode: code, force });
      setActionMsg(
        `週期 ${code} 補單完成，結算筆數：${result?.settledOrderCount ?? 0}`
      );
      setSettleConfirmCode('');
      loadRecords(page);
    } catch (err) {
      setSettleConfirmCode('');
      setActionMsg(err instanceof ApiError ? err.message : '補單失敗');
    } finally {
      setSettlingCode('');
    }
  }

  function handleSettleConfirm() {
    if (!settleConfirmCode) {
      return;
    }
    handleSettle(settleConfirmCode, false);
  }

  async function handleSyncResults() {
    setActionMsg('');
    const rangeError = validateContractRange(
      filters.contractTimeFromInput,
      filters.contractTimeToInput
    );
    if (rangeError) {
      setActionMsg(rangeError);
      return;
    }
    setSyncing(true);
    try {
      const result = await adminApi.syncFuturesRoundResults({
        contractTimeFrom: inputDateTimeToBodyApi(filters.contractTimeFromInput),
        contractTimeTo: inputDateTimeToBodyApi(filters.contractTimeToInput),
      });
      const scanned = result?.scannedCount ?? 0;
      const ok = result?.successCount ?? 0;
      const failed = result?.failedCount ?? 0;
      setActionMsg(
        `檢查開獎完成：掃描 ${scanned} 局，成功 ${ok} 局${failed > 0 ? `，失敗 ${failed} 局` : ''}`
      );
      loadRecords(page);
    } catch (err) {
      setActionMsg(err instanceof ApiError ? err.message : '檢查開獎失敗');
    } finally {
      setSyncing(false);
    }
  }

  function handleBatchSaved({ ok, total, failed }) {
    if (failed > 0) {
      setActionMsg(`批量配置完成：成功 ${ok}/${total}，${total - ok} 筆不可配置或失敗`);
    } else {
      setActionMsg(`批量配置完成：成功 ${ok}/${total}`);
    }
    loadRecords(page);
  }

  return (
    <div className="page-card">
      <h2>週期記錄</h2>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="marketSymbolCode">產品名稱（交易對）</label>
          <select
            id="marketSymbolCode"
            value={filters.marketSymbolCode}
            onChange={(e) => updateFilter('marketSymbolCode', e.target.value)}
          >
            <option value="">全部</option>
            {symbolOptions.map((item) => (
              <option key={item.code} value={item.code}>
                {item.code}
                {item.displayName ? ` (${item.displayName})` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="periodType">週期</label>
          <select
            id="periodType"
            value={filters.periodType}
            onChange={(e) => updateFilter('periodType', e.target.value)}
          >
            <option value="">全部</option>
            {FUTURES_ROUND_PERIODS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="state">週期狀態</label>
          <select
            id="state"
            value={filters.state}
            onChange={(e) => updateFilter('state', e.target.value)}
          >
            {FUTURES_ROUND_STATE_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="roundCode">週期 ID</label>
          <input
            id="roundCode"
            value={filters.roundCode}
            onChange={(e) => updateFilter('roundCode', e.target.value)}
            placeholder="支援模糊搜尋"
          />
        </div>

        <div className="form-field">
          <label htmlFor="overrideMethod">場控設置</label>
          <select
            id="overrideMethod"
            value={filters.overrideMethod}
            onChange={(e) => updateFilter('overrideMethod', e.target.value)}
          >
            {FUTURES_OVERRIDE_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="contractTimeFrom">合約時間起</label>
          <input
            id="contractTimeFrom"
            type="datetime-local"
            value={filters.contractTimeFromInput}
            onChange={(e) => handleContractFromChange(e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="contractTimeTo">合約時間迄</label>
          <input
            id="contractTimeTo"
            type="datetime-local"
            value={filters.contractTimeToInput}
            onChange={(e) => handleContractToChange(e.target.value)}
          />
          <span className="field-hint">合約時間跨度最多 {MAX_CONTRACT_QUERY_DAYS} 天</span>
        </div>

        <div className="form-field">
          <label htmlFor="assetClass">商品類型</label>
          <select
            id="assetClass"
            value={filters.assetClass}
            onChange={(e) => updateFilter('assetClass', e.target.value)}
          >
            {FUTURES_ASSET_CLASS_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
          <button
            type="button"
            className="btn btn-warning"
            onClick={handleSyncResults}
            disabled={syncing}
          >
            {syncing ? '檢查中…' : '檢查開獎結果'}
          </button>
          <button
            type="button"
            className="btn btn-success"
            onClick={() => setConfigOpen(true)}
            disabled={configurableSelectedCodes.length === 0}
            title={configurableSelectedCodes.length === 0 ? '請先勾選可配置週期' : ''}
          >
            批量配置
          </button>
        </div>
      </form>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}
      {actionMsg && (
        <div
          className={`alert ${
            actionMsg.includes('失敗') ? 'alert-error' : 'alert-success'
          }`}
        >
          {actionMsg}
        </div>
      )}

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allConfigurableSelected}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                  aria-label="全選可配置週期"
                />
              </th>
              <th>週期 ID</th>
              <th>合約時間</th>
              <th>產品名稱_週期</th>
              <th>商品類型</th>
              <th>週期狀態</th>
              <th>週期結果</th>
              <th>場控設置</th>
              <th>盈虧金額 (USDT)</th>
              <th>做多單數</th>
              <th>做空單數</th>
              <th>投注人數</th>
              <th>做多金額 (USDT)</th>
              <th>做空金額 (USDT)</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={15} className="empty-cell">
                  尚無紀錄
                </td>
              </tr>
            ) : (
              records.map((row) => {
                const configurable = isRoundConfigurable(row.state);
                const canSettle = isRoundEnded(row.endTime);
                return (
                <tr key={row.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedCodes.includes(row.roundCode)}
                      disabled={!configurable}
                      title={configurable ? '' : '僅進行中或待開獎週期可配置'}
                      onChange={(e) => toggleRow(row, e.target.checked)}
                    />
                  </td>
                  <td className="mono-cell">{row.roundCode || '—'}</td>
                  <td>{formatDateTime(row.endTime)}</td>
                  <td className="product-period-cell">
                    <div>產品名稱: {row.marketSymbolCode || '—'}</div>
                    <div>
                      周期:{' '}
                      {row.periodTypeLabel ||
                        FUTURES_ROUND_PERIOD_LABELS[row.periodType] ||
                        '—'}
                    </div>
                  </td>
                  <td>
                    {row.assetClass
                      ? MARKET_ASSET_CLASS_LABELS[row.assetClass] || row.assetClass
                      : '—'}
                  </td>
                  <td>
                    {row.stateLabel ||
                      FUTURES_ROUND_STATE_LABELS[row.state] ||
                      '—'}
                  </td>
                  <td>{row.roundResultLabel || '—'}</td>
                  <td>
                    {row.overrideMethod
                      ? OVERRIDE_LABELS[row.overrideMethod] || row.overrideMethod
                      : '—'}
                  </td>
                  <td>{formatAmount(row.profitLoss)}</td>
                  <td>{row.goLongCount ?? 0}</td>
                  <td>{row.goShortCount ?? 0}</td>
                  <td>{row.memberCount ?? '—'}</td>
                  <td>{formatAmount(row.goLongAmount)}</td>
                  <td>{formatAmount(row.goShortAmount)}</td>
                  <td className="action-cell">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={!configurable}
                      title={configurable ? '' : '僅進行中或待開獎週期可配置'}
                      onClick={() => setSingleConfigCode(row.roundCode)}
                    >
                      週期配置
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={!canSettle || settlingCode === row.roundCode}
                      title={!canSettle ? '週期尚未到期，無法補單' : ''}
                      onClick={() => setSettleConfirmCode(row.roundCode)}
                    >
                      {settlingCode === row.roundCode ? '處理中…' : '補單'}
                    </button>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(p) => loadRecords(p, filters)} />

      <FuturesRoundConfigModal
        roundCode={singleConfigCode}
        open={Boolean(singleConfigCode)}
        onClose={() => setSingleConfigCode('')}
        onSaved={() => loadRecords(page, filters)}
      />

      <FuturesRoundBatchConfigModal
        open={configOpen}
        roundCodes={configurableSelectedCodes}
        onClose={() => setConfigOpen(false)}
        onSaved={handleBatchSaved}
      />

      <ConfirmDialog
        open={Boolean(settleConfirmCode)}
        title="提示"
        detail={`週期 ID：${settleConfirmCode}`}
        message="是否對未開獎的期數進行補單？"
        confirming={Boolean(settlingCode)}
        onClose={() => {
          if (!settlingCode) {
            setSettleConfirmCode('');
          }
        }}
        onConfirm={handleSettleConfirm}
      />
    </div>
  );
}
